import {
    componentStyles,
    MediaDisplayType,
    MultiMediaDisplay,
    Text,
} from "@mallardbay/lib-react-components"
import React from "react"

import COPY from "~config/copy-constants"
import { useTabletMediaQueries } from "~components/shared/todo-lib-react-components/hooks/use-media-queries"
import Modal from "~components/shared/todo-lib-react-components/modal"
import { ResourceType } from "~graphql/generated/graphql"
import type {
    AtvProps,
    BlindProps,
    BoatProps,
    GearProps,
    VehicleProps,
    ResourceFieldsFragment,
} from "~graphql/generated/graphql"
import { formatCapacity } from "~utils/lodging-helpers"
import { formatEnumToTitleCase } from "~utils/formatting-helpers"

import ResourceInfoItem from "./resource-info-item"

export default function ResourceItemModal({
    resource,
    onClose,
}: {
    readonly resource: ResourceFieldsFragment
    readonly onClose: () => void
}) {
    const styles = useStyles()
    const images = resource.images?.length
        ? resource.images
        : [{ url: resource.cover_image }]

    const hasImages = Boolean(images.length)
    const { isLargerThanTablet } = useTabletMediaQueries()
    const mediaDisplayType = isLargerThanTablet
        ? MediaDisplayType.GRID
        : MediaDisplayType.CAROUSEL

    const resourceInfo = getResourceInfo({ resource })

    return (
        <Modal
            isOpen
            onClose={onClose}
            title={formatEnumToTitleCase(resource.type)}
            shouldAddPadding
        >
            {hasImages && (
                <MultiMediaDisplay type={mediaDisplayType} images={images} />
            )}
            <Text style={styles.name}>{resource.name}</Text>
            {resourceInfo.map(({ key, title, value }) => (
                <ResourceInfoItem key={key} title={title} value={value} />
            ))}
            <Text style={styles.title}>{COPY.RESOURCES.DESCRIPTION}</Text>
            <Text style={styles.description}>
                {resource.description || COPY.RESOURCES.NO_DESCRIPTION}
            </Text>
            <Text>{formatCapacity(resource)}</Text>
        </Modal>
    )
}

function useStyles() {
    return componentStyles({
        name: {
            fontWeight: 700,
            marginTop: 2,
            marginBottom: 2,
        },
        description: {
            marginBottom: 2,
            marginTop: 1,
        },
        title: {
            fontWeight: 700,
            marginBottom: 0.5,
            marginTop: 1,
        },
        descriptionContainer: {
            marginBottom: 2,
        },
    })
}

function getResourceInfo({
    resource,
}: {
    readonly resource: ResourceFieldsFragment
}) {
    const propsMap: PropsMap = {
        [ResourceType.Listing]: {},
        [ResourceType.Lodging]: {},
        [ResourceType.Vehicle]: resource.vehicle_props,
        [ResourceType.Boat]: resource.boat_props,
        [ResourceType.Blind]: resource.blind_props,
        [ResourceType.Atv]: resource.atv_props,
        [ResourceType.Gear]: resource.gear_props,
    }
    const typeProps = propsMap[resource.type] ?? {}

    return Object.entries(typeProps)
        .filter(
            ([key, value]) => isDisplayableValue(value) || key === "__typename"
        )
        .map(([key, value]) => ({
            key,
            title: key,
            value: value,
        }))
}

function isDisplayableValue(
    value: string | number | undefined | null
): value is string | number {
    return typeof value === "string" || typeof value === "number"
}

type PropsMapValue =
    | VehicleProps
    | BoatProps
    | BlindProps
    | AtvProps
    | GearProps
    | null
    | undefined

type PropsMap = Record<ResourceType, PropsMapValue>
